/**
 * Hook for making panels/modals draggable by their header.
 *
 * Shared across the app (comments panel, usages panels, add-element modal,
 * translation/metadata modals). The pointer position is tracked in absolute
 * viewport coordinates and is meant to be applied as `position: fixed` with
 * `left`/`top`.
 */
import { useState, useRef, useEffect, RefObject } from 'react'

export interface DraggablePanelState {
  position: { x: number; y: number }
  isDragging: boolean
  handleHeaderMouseDown: (e: React.MouseEvent) => void
}

export interface DraggablePanelOptions {
  /**
   * Element used to measure the panel for the drag offset (and bounds).
   * Falls back to the drag handle's parent element when omitted. Pass this
   * when the handle is nested inside the panel (e.g. an antd modal header).
   */
  panelRef?: RefObject<HTMLElement | null>
  /** Keep the panel within the viewport while dragging. */
  bounds?: boolean
}

/**
 * Custom hook to make a panel draggable.
 *
 * @param initialPosition - Optional initial position { x, y }
 * @param options - Optional explicit panelRef (measurement element) and bounds
 * @returns State and handlers for draggable functionality
 */
export const useDraggablePanel = (
  initialPosition: { x: number; y: number } = { x: 0, y: 0 },
  options: DraggablePanelOptions = {}
): DraggablePanelState => {
  const { panelRef, bounds = false } = options
  const [position, setPosition] = useState(initialPosition)
  const [isDragging, setIsDragging] = useState(false)
  const dragStart = useRef({ x: 0, y: 0 })
  const panelSize = useRef({ width: 0, height: 0 })

  const handleHeaderMouseDown = (e: React.MouseEvent) => {
    // Don't drag if clicking on buttons or interactive elements
    const target = e.target as HTMLElement
    if (
      target.tagName === 'BUTTON' ||
      target.tagName === 'INPUT' ||
      target.tagName === 'A' ||
      target.closest('button') ||
      target.closest('input') ||
      target.closest('a')
    ) {
      return
    }

    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)

    // Measure the panel: explicit ref when provided (needed when the drag
    // handle is nested), otherwise the handle's parent element.
    const panel =
      panelRef?.current ?? (e.currentTarget as HTMLElement).parentElement
    if (panel) {
      const rect = panel.getBoundingClientRect()
      dragStart.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      }
      panelSize.current = { width: rect.width, height: rect.height }
    }
  }

  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      let x = e.clientX - dragStart.current.x
      let y = e.clientY - dragStart.current.y

      if (bounds) {
        const maxX = Math.max(0, window.innerWidth - panelSize.current.width)
        const maxY = Math.max(0, window.innerHeight - panelSize.current.height)
        x = Math.min(Math.max(0, x), maxX)
        y = Math.min(Math.max(0, y), maxY)
      }

      setPosition({ x, y })
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, bounds])

  return {
    position,
    isDragging,
    handleHeaderMouseDown,
  }
}
